import React, { useState, useEffect, useRef } from "react";
import { User, Trophy, Calendar, ChevronRight, Search, ArrowLeft, Loader2, Check } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import {
    TournamentResponse,
    PlayerResponse,
    MatchResponse,
    entityTournamentsQueryOptions,
    entityTournamentMatchesQueryOptions,
    entityTournamentPlayersQueryOptions,
} from "@/api/entities";

export interface SelectedEntity {
    type: string;
    id: number;
    name: string;
}

interface EntityContextMenuProps {
    isOpen: boolean;
    position: { x: number; y: number };
    onSelect: (entities: SelectedEntity[]) => void;
    onClose: () => void;
}

type MenuView = "main" | "players" | "tournaments" | "matches";
type SubMenuState = {
    view: MenuView;
    selectedTournamentId?: number;
    selectedTournamentName?: string;
};

export const EntityContextMenu: React.FC<EntityContextMenuProps> = ({ isOpen, position, onSelect, onClose }) => {
    const [menuState, setMenuState] = useState<SubMenuState>({ view: "main" });
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedEntities, setSelectedEntities] = useState<SelectedEntity[]>([]);
    const menuRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);

    const needsTournaments = isOpen && (menuState.view === "tournaments" || menuState.view === "players");
    const { data: tournaments = [], isLoading: isTournamentsLoading } = useQuery({
        ...entityTournamentsQueryOptions,
        enabled: needsTournaments,
    });

    const lastTournament = [...tournaments].reverse().find((t) => t.matches.length > 0) ?? tournaments[tournaments.length - 1];
    const lastTournamentId = lastTournament?.id ?? 0;
    const lastTournamentName = lastTournament?.name ?? "";
    const { data: players = [], isLoading: isPlayersLoading } = useQuery({
        ...entityTournamentPlayersQueryOptions(lastTournamentId),
        enabled: isOpen && menuState.view === "players" && !!lastTournamentId,
    });

    const { data: matches = [], isLoading: isMatchesLoading } = useQuery({
        ...entityTournamentMatchesQueryOptions(menuState.selectedTournamentId ?? 0),
        enabled: isOpen && menuState.view === "matches" && !!menuState.selectedTournamentId,
    });

    const isLoading = isTournamentsLoading || isPlayersLoading || isMatchesLoading;

    useEffect(() => {
        if (isOpen) {
            setMenuState({ view: "main" });
            setSearchTerm("");
            setSelectedEntities([]);
            setTimeout(() => {
                searchInputRef.current?.focus();
            }, 100);
        }
    }, [isOpen]);

    const toggleEntitySelection = (type: string, id: number, name: string) => {
        setSelectedEntities((prev) => {
            const exists = prev.find((e) => e.type === type && e.id === id);
            if (exists) {
                return prev.filter((e) => !(e.type === type && e.id === id));
            } else {
                return [...prev, { type, id, name }];
            }
        });
    };

    const isEntitySelected = (type: string, id: number) => {
        return selectedEntities.some((e) => e.type === type && e.id === id);
    };

    const handleApplySelection = () => {
        if (selectedEntities.length > 0) {
            onSelect(selectedEntities);
            onClose();
        }
    };


    if (!isOpen) return null;

    const getPlayerFullName = (player: PlayerResponse) => {
        return player.firstName ? `${player.firstName} ${player.lastName}` : player.lastName;
    };

    const formatMatchName = (match: MatchResponse) => {
        return `${match.homeTeam.fullName} vs ${match.awayTeam.fullName}`;
    };

    const formatMatchDate = (dateString?: string) => {
        if (!dateString) return "TBD";
        return new Date(dateString).toLocaleDateString();
    };

    const filteredPlayers = players.filter((player) => {
        const fullName = getPlayerFullName(player);
        return fullName.toLowerCase().includes(searchTerm.toLowerCase());
    });

    const filteredTournaments = tournaments
        .filter(
            (tournament) =>
                tournament.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                tournament.group.toLowerCase().includes(searchTerm.toLowerCase())
        )
        .sort((a, b) => b.id - a.id);

    const filteredMatches = matches
        .filter((match) => {
            const matchName = formatMatchName(match);
            return matchName.toLowerCase().includes(searchTerm.toLowerCase());
        })
        .sort((a, b) => {
            if (!a.date && !b.date) return 0;
            if (!a.date) return 1;
            if (!b.date) return -1;
            return new Date(b.date).getTime() - new Date(a.date).getTime();
        });

    const handleBack = () => {
        if (menuState.view === "matches" && menuState.selectedTournamentId) {
            setMenuState({ view: "tournaments" });
        } else {
            setMenuState({ view: "main" });
        }
        setSearchTerm("");
    };

    const handleTournamentSelect = (tournament: TournamentResponse) => {
        setMenuState({
            view: "matches",
            selectedTournamentId: tournament.id,
            selectedTournamentName: tournament.name,
        });
        setSearchTerm("");
    };

    const renderMainMenu = () => (
        <div className="p-2">
            <div className="text-xs text-on-surface-variant mb-2 px-2">Select entity type:</div>
            <div className="space-y-1">
                <button
                    onClick={() => setMenuState({ view: "players" })}
                    className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-surface-container transition-colors text-left cursor-pointer"
                >
                    <User className="w-4 h-4 text-blue-400" />
                    <span className="text-sm text-on-surface">Players</span>
                    <ChevronRight className="w-3 h-3 text-on-surface-variant ml-auto" />
                </button>
                <button
                    onClick={() => setMenuState({ view: "tournaments" })}
                    className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-surface-container transition-colors text-left cursor-pointer"
                >
                    <Trophy className="w-4 h-4 text-yellow-400" />
                    <span className="text-sm text-on-surface">Tournaments</span>
                    <ChevronRight className="w-3 h-3 text-on-surface-variant ml-auto" />
                </button>
            </div>
        </div>
    );

    const renderPlayersMenu = () => (
        <div className="p-2">
            <div className="flex items-center gap-2 mb-2">
                <button onClick={handleBack} className="p-1 rounded hover:bg-surface-container transition-colors cursor-pointer">
                    <ArrowLeft className="w-3 h-3 text-on-surface-variant" />
                </button>
                <div className="text-xs text-on-surface-variant">Select player:</div>
                {isLoading && <Loader2 className="w-3 h-3 animate-spin text-primary ml-auto" />}
            </div>
            {lastTournamentName && (
                <div className="text-xs text-on-surface-variant px-2 mb-2 truncate">From: {lastTournamentName}</div>
            )}
            <div className="relative mb-2">
                <Search className="w-3 h-3 text-on-surface-variant absolute left-2 top-2.5" />
                <input
                    ref={searchInputRef}
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search players..."
                    className="w-full pl-7 pr-3 py-2 text-xs bg-surface-container border border-outline-variant rounded-lg focus:outline-none focus:ring-1 focus:ring-primary text-on-surface placeholder:text-on-surface-variant"
                />
            </div>
            <div className="max-h-48 overflow-y-auto space-y-1">
                {isLoading ? (
                    <div className="flex items-center justify-center py-4">
                        <Loader2 className="w-4 h-4 animate-spin text-primary" />
                        <span className="text-sm text-on-surface-variant ml-2">Loading players...</span>
                    </div>
                ) : filteredPlayers.length === 0 ? (
                    <div className="text-sm text-on-surface-variant text-center py-4">
                        {searchTerm ? "No players found" : "No players available"}
                    </div>
                ) : (
                    filteredPlayers.map((player) => {
                        const isSelected = isEntitySelected("player", player.id);
                        return (
                            <button
                                key={player.id}
                                onClick={() => toggleEntitySelection("player", player.id, getPlayerFullName(player))}
                                className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-surface-container transition-colors text-left cursor-pointer"
                            >
                                <div
                                    className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                                        isSelected ? "bg-primary border-primary" : "border-outline bg-transparent"
                                    }`}
                                >
                                    {isSelected && <Check className="w-3 h-3 text-white" />}
                                </div>
                                <User className="w-3 h-3 text-blue-400 flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm text-on-surface truncate">{getPlayerFullName(player)}</div>
                                </div>
                            </button>
                        );
                    })
                )}
            </div>
        </div>
    );

    const renderTournamentsMenu = () => (
        <div className="p-2">
            <div className="flex items-center gap-2 mb-2">
                <button onClick={handleBack} className="p-1 rounded hover:bg-surface-container transition-colors cursor-pointer">
                    <ArrowLeft className="w-3 h-3 text-on-surface-variant" />
                </button>
                <div className="text-xs text-on-surface-variant">Select tournament:</div>
                {isLoading && <Loader2 className="w-3 h-3 animate-spin text-primary ml-auto" />}
            </div>
            <div className="relative mb-2">
                <Search className="w-3 h-3 text-on-surface-variant absolute left-2 top-2.5" />
                <input
                    ref={searchInputRef}
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search tournaments..."
                    className="w-full pl-7 pr-3 py-2 text-xs bg-surface-container border border-outline-variant rounded-lg focus:outline-none focus:ring-1 focus:ring-primary text-on-surface placeholder:text-on-surface-variant"
                />
            </div>
            <div className="max-h-48 overflow-y-auto space-y-1">
                {isLoading ? (
                    <div className="flex items-center justify-center py-4">
                        <Loader2 className="w-4 h-4 animate-spin text-primary" />
                        <span className="text-sm text-on-surface-variant ml-2">Loading tournaments...</span>
                    </div>
                ) : filteredTournaments.length === 0 ? (
                    <div className="text-sm text-on-surface-variant text-center py-4">
                        {searchTerm ? "No tournaments found" : "No tournaments available"}
                    </div>
                ) : (
                    filteredTournaments.map((tournament) => {
                        const isSelected = isEntitySelected("tournament", tournament.id);
                        return (
                            <div
                                key={tournament.id}
                                className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-surface-container transition-colors"
                            >
                                <button
                                    onClick={() => toggleEntitySelection("tournament", tournament.id, tournament.name)}
                                    className="flex items-center gap-2 cursor-pointer"
                                >
                                    <div
                                        className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                                            isSelected
                                                ? "bg-primary border-primary"
                                                : "border-outline bg-transparent"
                                        }`}
                                    >
                                        {isSelected && <Check className="w-3 h-3 text-white" />}
                                    </div>
                                    <Trophy className="w-3 h-3 text-yellow-400 flex-shrink-0" />
                                </button>
                                <div className="flex-1 min-w-0" onClick={() => handleTournamentSelect(tournament)}>
                                    <div className="text-sm text-on-surface truncate cursor-pointer">
                                        {tournament.name}
                                    </div>
                                    <div className="text-xs text-on-surface-variant truncate cursor-pointer">
                                        {tournament.group} {tournament.year ? `• ${tournament.year}` : ""}
                                    </div>
                                </div>
                                <button
                                    onClick={() => handleTournamentSelect(tournament)}
                                    className="p-1 hover:bg-surface-container rounded transition-colors cursor-pointer"
                                >
                                    <ChevronRight className="w-3 h-3 text-on-surface-variant" />
                                </button>
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );

    const renderMatchesMenu = () => (
        <div className="p-2">
            <div className="flex items-center gap-2 mb-2">
                <button onClick={handleBack} className="p-1 rounded hover:bg-surface-container transition-colors cursor-pointer">
                    <ArrowLeft className="w-3 h-3 text-on-surface-variant" />
                </button>
                <div className="text-xs text-on-surface-variant">Select match:</div>
                {isLoading && <Loader2 className="w-3 h-3 animate-spin text-primary ml-auto" />}
            </div>
            <div className="text-xs text-on-surface-variant px-2 mb-2 truncate">{menuState.selectedTournamentName}</div>
            <div className="relative mb-2">
                <Search className="w-3 h-3 text-on-surface-variant absolute left-2 top-2.5" />
                <input
                    ref={searchInputRef}
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search matches..."
                    className="w-full pl-7 pr-3 py-2 text-xs bg-surface-container border border-outline-variant rounded-lg focus:outline-none focus:ring-1 focus:ring-primary text-on-surface placeholder:text-on-surface-variant"
                />
            </div>
            <div className="max-h-48 overflow-y-auto space-y-1">
                {isLoading ? (
                    <div className="flex items-center justify-center py-4">
                        <Loader2 className="w-4 h-4 animate-spin text-primary" />
                        <span className="text-sm text-on-surface-variant ml-2">Loading matches...</span>
                    </div>
                ) : filteredMatches.length === 0 ? (
                    <div className="text-sm text-on-surface-variant text-center py-4">
                        {searchTerm ? "No matches found" : "No matches available"}
                    </div>
                ) : (
                    filteredMatches.map((match) => {
                        const isSelected = isEntitySelected("match", match.id);
                        return (
                            <button
                                key={match.id}
                                onClick={() => toggleEntitySelection("match", match.id, formatMatchName(match))}
                                className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-surface-container transition-colors text-left cursor-pointer"
                            >
                                <div
                                    className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                                        isSelected ? "bg-primary border-primary" : "border-outline bg-transparent"
                                    }`}
                                >
                                    {isSelected && <Check className="w-3 h-3 text-white" />}
                                </div>
                                <Calendar className="w-3 h-3 text-green-400 flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm text-on-surface truncate">{formatMatchName(match)}</div>
                                    <div className="text-xs text-on-surface-variant truncate">
                                        {formatMatchDate(match.date)}
                                        {match.homeGoals !== undefined &&
                                            match.awayGoals !== undefined &&
                                            ` • ${match.homeGoals}-${match.awayGoals}`}
                                    </div>
                                </div>
                            </button>
                        );
                    })
                )}
            </div>
        </div>
    );

    return (
        <>
            {/* Backdrop */}
            <div className="fixed inset-0 z-40" onClick={onClose} />

            {/* Menu */}
            <div
                ref={menuRef}
                className="fixed z-50 bg-surface-container-lowest border border-outline-variant/20 rounded-md shadow-[0_8px_32px_rgba(5,0,102,0.12)] min-w-[280px] max-w-[400px] flex flex-col"
                style={{
                    left: position.x,
                    top: position.y - 10,
                    transform: "translateY(-100%)",
                }}
            >
                <div className="flex-1 overflow-hidden">
                    {menuState.view === "main" && renderMainMenu()}
                    {menuState.view === "players" && renderPlayersMenu()}
                    {menuState.view === "tournaments" && renderTournamentsMenu()}
                    {menuState.view === "matches" && renderMatchesMenu()}
                </div>

                {/* Footer with Done button - only show when not on main menu and has selections */}
                {menuState.view !== "main" && selectedEntities.length > 0 && (
                    <div className="border-t border-outline-variant/20 p-2">
                        <div className="flex items-center justify-between gap-2">
                            <span className="text-xs text-on-surface-variant px-2">{selectedEntities.length} selected</span>
                            <button
                                onClick={handleApplySelection}
                                className="px-4 py-2 bg-primary hover:bg-primary/90 text-white text-xs font-headline font-bold uppercase tracking-wider rounded-sm transition-colors cursor-pointer"
                            >
                                Done
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};
